import { ButtonERP } from "@finanedi.es/storybook"
import { GetFacturasAceptablesDtoProps } from "api/DTOs/GetFacturasAceptablesDto"
import { GetPagaresAceptablesDtoProps } from "api/DTOs/GetPagaresAceptablesDto"
import { ESTADO_VERIFICACION_EFECTO } from "api/Types/EstadoVerificacionEfecto"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import {
  EstadoFinanacionDetailResponseProps,
  FINANCIACION_TYPE,
} from "features/Validaciones/pages/Financiacion/constant"
import { FLOW_STATUS_AUT_OPTIONS } from "features/Validaciones/pages/Financiacion/utils"
import { Dropdown, DropdownChangeParams } from "primereact/dropdown"
import { InputTextarea } from "primereact/inputtextarea"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation } from "react-router-dom"

interface FinanciacionVerificacionProps {
  documentsToValidate:
    | GetFacturasAceptablesDtoProps[]
    | GetPagaresAceptablesDtoProps[]
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  handleSelect: (event: DropdownChangeParams) => void
  handleChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  dataToValidate: EstadoFinanacionDetailResponseProps
  type: number
}

const FinanciacionVerification = ({
  documentsToValidate,
  handleSubmit,
  handleSelect,
  handleChange,
  dataToValidate,
  type,
}: FinanciacionVerificacionProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { state } = useLocation()
  const {
    activeCompany: { razonSocial: empresaActivaRazonSocial },
  } = useUsuarioEmpresaActiva()

  const uniqueDocumentsToValidate = useMemo(() => {
    return Array.from(
      new Set(
        documentsToValidate
          .map(
            (
              doc: GetFacturasAceptablesDtoProps | GetPagaresAceptablesDtoProps
            ) => doc.librador?.razonSocial ?? ""
          )
          .filter((razonSocial: string): boolean => Boolean(razonSocial))
      )
    ).join(", ")
  }, [documentsToValidate])

  const goBack = () => {
    state?.prevPath.toLowerCase().includes("/contactos/")
      ? history.goBack()
      : history.push({
          pathname: "/validaciones",
          state: {
            selectedTab: state?.selectedTab,
          },
        })
  }

  return (
    <form
      action='submit'
      onSubmit={handleSubmit}
    >
      <div className='tw-p-6 tw-w-full tw-text-sm'>
        <div className='tw-rounded tw-bg-white tw-shadow tw-flex tw-flex-col tw-w-full tw-p-4'>
          <h6 className='tw-text-left tw-text-text tw-text-base tw-font-medium'>
            {type === FINANCIACION_TYPE.FACTURAS
              ? `${t("validaciones.financiable.idView.verifiedInvoces")}`
              : `${t("validaciones.financiable.idView.verifiedPagares")}`}
          </h6>
          <div className='tw-w-full tw-mt-3 tw-text-left'>
            <label htmlFor='importeDesde'>
              {t("validaciones.financiable.idView.statusLabel")}
            </label>
            <Dropdown
              placeholder={`${t(
                "validaciones.financiable.idView.placeholderStatus"
              )}`}
              options={FLOW_STATUS_AUT_OPTIONS(t)}
              className='tw-rounded tw-w-full tw-text-sm'
              name='tipoDocumento'
              id='tipoDocumento'
              onChange={handleSelect}
              value={dataToValidate?.estadoVerificacion}
            />

            {dataToValidate?.estadoVerificacion ===
              ESTADO_VERIFICACION_EFECTO.VERIFICADO && (
              <p className='tw-mt-4 tw-w-full'>
                <span className='tw-font-medium'>
                  {empresaActivaRazonSocial}
                </span>
                {documentsToValidate?.length < 2
                  ? type === FINANCIACION_TYPE.FACTURAS
                    ? t(
                        "validaciones.financiable.idView.factura.textMsgOneSing"
                      )
                    : t("validaciones.financiable.idView.pagare.textMsgOneSing")
                  : type === FINANCIACION_TYPE.FACTURAS
                  ? t("validaciones.financiable.idView.factura.textMsgOnePlu")
                  : t("validaciones.financiable.idView.pagare.textMsgOnePlu")}
                <span className='tw-font-medium'>
                  {uniqueDocumentsToValidate}
                </span>
                {documentsToValidate?.length < 2
                  ? type === FINANCIACION_TYPE.FACTURAS
                    ? t(
                        "validaciones.financiable.idView.factura.textMsgTwoSing"
                      )
                    : t("validaciones.financiable.idView.pagare.textMsgTwoSing")
                  : type === FINANCIACION_TYPE.FACTURAS
                  ? t("validaciones.financiable.idView.factura.textMsgTwoPlu")
                  : t("validaciones.financiable.idView.pagare.textMsgTwoPlu")}
              </p>
            )}

            {dataToValidate?.estadoVerificacion ===
              ESTADO_VERIFICACION_EFECTO.NO_VERIFICADO && (
              <div className='tw-mt-4 tw-w-full tw-flex tw-flex-col'>
                <label htmlFor='observaciones'>
                  {t("validaciones.financiable.idView.observationLabel")}
                </label>
                <InputTextarea
                  autoFocus
                  rows={5}
                  cols={10}
                  autoResize
                  id='observaciones'
                  name='observaciones'
                  aria-describedby='username-help'
                  value={dataToValidate?.observaciones}
                  onChange={handleChange}
                  autoComplete='off'
                />
              </div>
            )}
          </div>

          <div className='tw-flex tw-flex-row tw-items-center tw-justify-end tw-gap-2 tw-mt-4'>
            <ButtonERP
              customize={{
                size: "md",
                type: "transparent",
              }}
              onClick={goBack}
              type='button'
              label={`${t("validaciones.financiable.idView.cancelLabel")}`}
            />
            <ButtonERP
              customize={{
                size: "md",
                type:
                  dataToValidate?.estadoVerificacion ===
                  ESTADO_VERIFICACION_EFECTO.NO_VERIFICADO
                    ? "danger"
                    : "secondary",
              }}
              label={
                dataToValidate?.estadoVerificacion ===
                ESTADO_VERIFICACION_EFECTO.NO_VERIFICADO
                  ? `${t("validaciones.financiable.idView.rejectButton")}`
                  : `${t("validaciones.financiable.idView.aceptButton")}`
              }
              disabled={dataToValidate?.estadoVerificacion === null}
              type='submit'
            />
          </div>
        </div>
      </div>
    </form>
  )
}

export default FinanciacionVerification
